import { onMounted, ref, type Ref } from "vue";
import type {
	DefaultParamsType,
	ServiceParams,
	UsePaginationOptions,
	UsePaginationReturn,
} from "./types";

/**
 * usePagination Hook（支持额外查询参数）
 * @param service 异步请求函数，接收分页参数 + query 参数
 * @param options 配置项
 */
export function usePagination<C extends string, S extends string, D = AObject, P = AObject>(
	service: (params: ServiceParams<P, C, S>) => Promise<HttpResponse<ListDataResponse<D>>>,
	options: UsePaginationOptions<P, C, S> = {}
): UsePaginationReturn<D, P, C, S> {
	const { defaultParams = {} as ServiceParams<P, C, S>, manual = false } = options;
	const pagination = options.pagination ?? {};
	const {
		currentKey = "page" as C,
		pageSizeKey = "page_size" as S,
		totalKey = "total",
		dataKey = "list",
	} = pagination;

	const defaultQuery = { ...defaultParams } as ServiceParams<P, C, S>;

	if (defaultQuery) {
		if (currentKey && defaultQuery[currentKey]) {
			delete defaultQuery[currentKey];
		}

		if (pageSizeKey && defaultQuery[pageSizeKey]) {
			delete defaultQuery[pageSizeKey];
		}
	}

	const current = ref(defaultParams[currentKey] || 1);
	const pageSize = ref(defaultParams[pageSizeKey] || 10);
	const query = ref<P>(defaultQuery as P);

	const total = ref(0);
	const data: Ref<D[]> = ref([]);
	const loading = ref(false);

	const errorInfo = ref<null | Error>(null);

	const run = async (params: DefaultParamsType<P, C, S> = {}, isReset?: boolean) => {
		loading.value = true;

		try {
			const cloneParams = { ...params };

			current.value = cloneParams?.[currentKey] || current.value;
			pageSize.value = cloneParams?.[pageSizeKey] || pageSize.value;

			if (!isReset) {
				query.value = { ...query.value, ...cloneParams } as P;
			} else {
				query.value = { ...cloneParams } as P;
			}

			if (params) {
				if (cloneParams[currentKey]) {
					delete cloneParams[currentKey];
				}

				if (cloneParams[pageSizeKey]) {
					delete cloneParams[pageSizeKey];
				}
			}

			const res = await service({
				...query.value,
				[currentKey]: current.value,
				[pageSizeKey]: pageSize.value,
			} as ServiceParams<P, C, S>);

			if (res.code !== window.$config.successCode) {
				throw res;
			}

			total.value = res.data[totalKey] || 0;
			data.value = res.data[dataKey] || [];
			errorInfo.value = null;
		} catch (_error) {
			errorInfo.value = _error as Error;
		} finally {
			loading.value = false;
		}
	};

	/**
	 * 刷新数据，重置页码、每页数量和查询参数
	 */
	const refresh = () => {
		run();
	};

	const reset = (resetParams?: DefaultParamsType<P, C, S>) => {
		const params = resetParams || options.defaultParams || {};

		run(params, true);
	};

	onMounted(() => {
		// 默认自动触发请求
		if (!manual) {
			run();
		}
	});

	return {
		data,
		loading,
		current,
		pageSize,
		total,
		errorInfo,
		run,
		refresh,
		reset,
	};
}
